/* Eloss — product gallery
 *
 * Spec: §F.3, §F.1, §N.5, §M (INP), §P.5.
 *
 * The gallery is a scroll container in every layout — a stacked column above
 * 990 px, a 2-up grid between 750 and 989, a scroll-snap swipe strip below
 * 750. Because it is always scrolling, "which image is showing" is derived
 * from an IntersectionObserver rather than from click state, so the answer is
 * the same whether the visitor clicked a thumbnail, swiped, or scrolled the
 * page.
 *
 * §F.3, "Variant selection snaps to the variant's media (never filters the
 * gallery)": the media list is fixed for the life of the page. On
 * eloss:variant:changed this element scrolls to the matching media and nothing
 * is added, removed or hidden.
 *
 * §N.5, "The carousel live region announces position only": the counter says
 * "Slika 2 od 6" and never enumerates the list.
 *
 * The lightbox (§F.3) is a dynamic import() on first activation (§P.5), so a
 * visitor who never opens it never downloads it.
 */

(() => {
  'use strict';

  const Eloss = window.Eloss;
  if (!Eloss) return;

  Eloss.define(
    'eloss-product-gallery',
    class ElossProductGallery extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('[data-gallery-viewport]');
        this.items = Array.from(this.querySelectorAll('[data-media-id]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-media-target]'));
        this.counter = this.querySelector('[data-gallery-counter]');
        if (!this.viewport || !this.items.length) return;

        this.onThumbClick = (event) =>
          this.scrollToMedia(event.currentTarget.dataset.mediaTarget);
        this.thumbs.forEach((thumb) =>
          thumb.addEventListener('click', this.onThumbClick)
        );

        this.onVariantChanged = (event) => this.handleVariantChanged(event);
        document.addEventListener(Eloss.EVENTS.variantChanged, this.onVariantChanged);

        if (this.dataset.lightboxEnabled === 'true') {
          this.onZoomClick = (event) => this.openLightbox(event);
          this.addEventListener('click', this.onZoomClick);
        }

        this.observeVisibility();
      }

      disconnectedCallback() {
        this.thumbs?.forEach((thumb) =>
          thumb.removeEventListener('click', this.onThumbClick)
        );
        document.removeEventListener(
          Eloss.EVENTS.variantChanged,
          this.onVariantChanged
        );
        if (this.onZoomClick) this.removeEventListener('click', this.onZoomClick);
        this.observer?.disconnect();
        this.observer = null;
        this.items = null;
        this.thumbs = null;
      }

      /**
       * Position is derived from what is actually on screen, so it stays
       * correct for scroll, swipe and thumbnail alike. Threshold 0.6 so a
       * half-scrolled strip does not flicker between two indices.
       */
      observeVisibility() {
        if (!('IntersectionObserver' in window)) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const index = this.items.indexOf(entry.target);
              if (index >= 0) this.setActive(index);
            });
          },
          { root: this.viewport, threshold: 0.6 }
        );

        this.items.forEach((item) => this.observer.observe(item));
      }

      setActive(index) {
        if (this.activeIndex === index) return;
        this.activeIndex = index;

        const mediaId = this.items[index]?.dataset.mediaId;
        this.thumbs.forEach((thumb) => {
          if (thumb.dataset.mediaTarget === mediaId) {
            thumb.setAttribute('aria-current', 'true');
          } else {
            thumb.removeAttribute('aria-current');
          }
        });

        if (this.counter && this.counter.dataset.template) {
          this.counter.textContent = this.counter.dataset.template
            .replace('__INDEX__', String(index + 1))
            .replace('__COUNT__', String(this.items.length));
        }
      }

      scrollToMedia(mediaId) {
        const target = this.items.find((item) => item.dataset.mediaId === mediaId);
        if (!target) return;
        target.scrollIntoView({
          behavior: Eloss.prefersReducedMotion() ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }

      handleVariantChanged(event) {
        if (event.detail?.sectionId !== this.dataset.sectionId) return;
        /* The section swap re-renders the info column, not the gallery, so the
           new variant's media id is read from the freshly rendered picker. */
        const root = document.querySelector(
          `.main-product[data-section-id="${this.dataset.sectionId}"]`
        );
        const mediaId = root?.querySelector('.main-product__info')?.dataset
          .variantMedia;
        if (mediaId) this.scrollToMedia(mediaId);
      }

      async openLightbox(event) {
        const trigger = event.target.closest('[data-lightbox-open]');
        if (!trigger) return;
        event.preventDefault();

        try {
          const module = await import(
            /* webpackIgnore: true */ this.dataset.lightboxSrc
          );
          module.open(this, trigger.dataset.lightboxOpen);
        } catch (error) {
          /* The zoom is an enhancement on top of an already-visible image.
             If it cannot load, the page is still complete. */
          console.warn('Eloss: lightbox unavailable', error);
        }
      }
    }
  );
})();
